import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, IconButton,
    Select, MenuItem, FormControl, InputLabel,
    Box, Divider, CircularProgress, Paper, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { MessageTemplate } from '../../../../../shared/models';
import {
    getVenueMessageTemplates,
    createMessageTemplate,
    updateMessageTemplate,
    deleteMessageTemplate,
    getAvailableTemplateVariables
} from '../../../lib/messageTemplates';

interface MessageTemplateSelectorProps {
    venueId: string;
    onSelectTemplate: (templateContent: string) => void;
    isOpen: boolean;
    onClose: () => void;
}

export default function MessageTemplateSelector({
    venueId,
    onSelectTemplate,
    isOpen,
    onClose
}: MessageTemplateSelectorProps) {
    const [templates, setTemplates] = useState<MessageTemplate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

    // State for template editor
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<'create' | 'update'>('create');
    const [editingTemplate, setEditingTemplate] = useState<{
        id?: string;
        name: string;
        content: string;
        category?: string;
    }>({
        name: '',
        content: ''
    });

    // State for delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    // Load templates when component mounts or venueId changes
    useEffect(() => {
        if (isOpen && venueId) {
            loadTemplates();
        }
    }, [venueId, isOpen]);

    // Load templates from Firestore
    const loadTemplates = async () => {
        setLoading(true);
        setError(null);

        try {
            const templateData = await getVenueMessageTemplates(venueId);
            setTemplates(templateData);

            // If templates exist and none is selected, select the first one
            if (templateData.length > 0 && !selectedTemplateId) {
                setSelectedTemplateId(templateData[0].ID);
            }
        } catch (err) {
            console.error('Error loading templates:', err);
            setError('Failed to load message templates');
        } finally {
            setLoading(false);
        }
    };

    // Handle selecting a template
    const handleSelectTemplate = (templateId: string) => {
        setSelectedTemplateId(templateId);
    };

    // Handle using the selected template
    const handleUseTemplate = () => {
        const selectedTemplate = templates.find(t => t.ID === selectedTemplateId);
        if (selectedTemplate) {
            onSelectTemplate(selectedTemplate.Content);
            onClose();
        }
    };

    // Start creating a new template
    const handleCreateNew = () => {
        setEditingTemplate({
            name: '',
            content: ''
        });
        setEditMode('create');
        setIsEditing(true);
    };

    // Start editing an existing template
    const handleEditTemplate = (templateId: string) => {
        const templateToEdit = templates.find(t => t.ID === templateId);
        if (templateToEdit) {
            setEditingTemplate({
                id: templateToEdit.ID,
                name: templateToEdit.Name,
                content: templateToEdit.Content,
                category: templateToEdit.Category
            });
            setEditMode('update');
            setIsEditing(true);
        }
    };

    // Handle saving a template (create or update)
    const handleSaveTemplate = async () => {
        if (!editingTemplate.name || !editingTemplate.content) {
            setError('Template name and content are required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (editMode === 'create') {
                // Create new template with proper handling of optional fields
                const templateData: {
                    VenueID: string;
                    Name: string;
                    Content: string;
                    Category?: string;
                    CreatedAt: string;
                    LastUpdatedAt: string;
                } = {
                    VenueID: venueId,
                    Name: editingTemplate.name,
                    Content: editingTemplate.content,
                    CreatedAt: '', // Will be set by the server
                    LastUpdatedAt: '' // Will be set by the server
                };

                // Only add Category if it exists and is not an empty string
                if (editingTemplate.category) {
                    templateData.Category = editingTemplate.category;
                }

                await createMessageTemplate(templateData);
            } else {
                // Update existing template with proper handling of optional fields
                if (editingTemplate.id) {
                    const updateData: {
                        Name: string;
                        Content: string;
                        Category?: string | null;
                    } = {
                        Name: editingTemplate.name,
                        Content: editingTemplate.content
                    };

                    // Only add Category if it exists and is not an empty string
                    if (editingTemplate.category) {
                        updateData.Category = editingTemplate.category;
                    } else {
                        // If category is empty/undefined and we're updating, set it to null 
                        // to remove it from Firebase (null is accepted by Firebase, undefined is not)
                        updateData.Category = null;
                    }

                    await updateMessageTemplate(editingTemplate.id, updateData);
                }
            }

            // Reload templates after save
            await loadTemplates();
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving template:', err);
            setError('Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    // Handle deleting a template
    const handleDeleteTemplate = async () => {
        if (!selectedTemplateId) return;

        setLoading(true);
        setError(null);

        try {
            await deleteMessageTemplate(selectedTemplateId);
            setShowDeleteConfirm(false);

            // Reload templates after delete
            await loadTemplates();
            // Reset selected template
            setSelectedTemplateId('');
        } catch (err) {
            console.error('Error deleting template:', err);
            setError('Failed to delete template');
        } finally {
            setLoading(false);
        }
    };

    // Get the template variables that can be used
    const templateVariables = getAvailableTemplateVariables();

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#1a1a2e',
                    backgroundImage: 'linear-gradient(rgba(26, 26, 46, 0.9), rgba(26, 26, 46, 0.95))',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(224, 177, 241, 0.2)',
                    borderRadius: '16px',
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: 'transparent',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                borderBottom: '1px solid rgba(224, 177, 241, 0.2)'
            }}>
                <Typography variant="h6" fontFamily='Inter' fontWeight={600}>Message Templates</Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    variant="outlined"
                    sx={{
                        color: '#C0C0DD',
                        borderColor: 'rgba(224, 177, 241, 0.5)',
                        '&:hover': {
                            backgroundColor: 'rgba(224, 177, 241, 0.1)',
                            borderColor: '#C0C0DD'
                        }
                    }}
                >
                    New Template
                </Button>
            </DialogTitle>

            <DialogContent sx={{
                bgcolor: 'transparent',
                color: 'white',
                pt: 3,
                flex: '1 1 auto',
                overflow: 'auto'
            }}>
                {loading && !isEditing ? (
                    <Box className="flex justify-center my-4">
                        <CircularProgress sx={{ color: '#C0C0DD' }} />
                    </Box>
                ) : error ? (
                    <Box className="my-4">
                        <Typography color="error" variant="body1">{error}</Typography>
                        <Button
                            variant="outlined"
                            onClick={loadTemplates}
                            sx={{
                                mt: 2,
                                borderColor: '#C0C0DD',
                                color: '#C0C0DD',
                                '&:hover': {
                                    backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                    borderColor: '#d396ed'
                                }
                            }}
                        >
                            Retry
                        </Button>
                    </Box>
                ) : showDeleteConfirm ? (
                    <Box className="my-4 text-center py-8">
                        <Typography variant="h6" fontFamily='Inter' fontWeight={600} className="mb-2">
                            Delete Template?
                        </Typography>
                        <Typography variant="body1" className="mb-4">
                            Are you sure you want to delete this template? This action cannot be undone.
                        </Typography>
                        <Box className="flex justify-center gap-4">
                            <Button
                                variant="outlined"
                                onClick={() => setShowDeleteConfirm(false)}
                                sx={{
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    color: 'rgba(255,255,255,0.7)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.05)'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleDeleteTemplate}
                                sx={{
                                    backgroundColor: '#ff5757',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#e04545',
                                    }
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                ) : isEditing ? (
                    <Box className="my-3">
                        <Box className="mb-5">
                            <Typography variant="subtitle1" fontFamily='Inter' fontWeight={600} className="mb-2">
                                {editMode === 'create' ? 'Create New Template' : 'Edit Template'}
                            </Typography>
                            <TextField
                                label="Template Name"
                                value={editingTemplate.name}
                                onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                size="small"
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': { borderColor: 'rgba(224, 177, 241, 0.5)' },
                                        '&:hover fieldset': { borderColor: '#C0C0DD' },
                                        '&.Mui-focused fieldset': { borderColor: '#C0C0DD' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#C0C0DD' }
                                }}
                            />

                            <FormControl
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                size="small"
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': { borderColor: 'rgba(224, 177, 241, 0.5)' },
                                        '&:hover fieldset': { borderColor: '#C0C0DD' },
                                        '&.Mui-focused fieldset': { borderColor: '#C0C0DD' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#C0C0DD' }
                                }}
                            >
                                <InputLabel>Category (Optional)</InputLabel>
                                <Select
                                    value={editingTemplate.category || ''}
                                    onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })}
                                    label="Category (Optional)"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="General">General</MenuItem>
                                    <MenuItem value="No Minimum">No Minimum</MenuItem>
                                    <MenuItem value="Semi-Private">Semi-Private</MenuItem>
                                    <MenuItem value="Private">Private</MenuItem>
                                    <MenuItem value="Holiday">Holiday</MenuItem>
                                    <MenuItem value="Weekend">Weekend</MenuItem>
                                    <MenuItem value="Special">Special</MenuItem>
                                </Select>
                            </FormControl>

                            <Box mb={2}>
                                <Typography variant="subtitle2" className="mb-1" fontFamily='Inter' fontWeight={600} sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                    Template Variables
                                </Typography>
                                <Box className="flex flex-wrap gap-1">
                                    {Object.entries(templateVariables).map(([variable, description]) => (
                                        <Tooltip key={variable} title={description} arrow placement="top">
                                            <div
                                                style={{
                                                    padding: '2px 12px',
                                                    backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                                    color: '#C0C0DD',
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    cursor: 'pointer',
                                                }}
                                                className="hover:bg-opacity-20"
                                                onClick={() => {
                                                    // Insert the variable at the cursor position in the text field
                                                    const cursorPos = document.activeElement as HTMLTextAreaElement;
                                                    if (cursorPos && cursorPos.selectionStart !== undefined) {
                                                        const start = cursorPos.selectionStart;
                                                        const end = cursorPos.selectionEnd;
                                                        const text = editingTemplate.content;
                                                        const newText = text.substring(0, start) + variable + text.substring(end);
                                                        setEditingTemplate({ ...editingTemplate, content: newText });
                                                    } else {
                                                        setEditingTemplate({ ...editingTemplate, content: editingTemplate.content + ' ' + variable });
                                                    }
                                                }}
                                            >
                                                {variable}
                                            </div>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>

                            <TextField
                                label="Template Content"
                                value={editingTemplate.content}
                                onChange={(e) => setEditingTemplate({ ...editingTemplate, content: e.target.value })}
                                multiline
                                rows={6}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': { borderColor: 'rgba(224, 177, 241, 0.5)' },
                                        '&:hover fieldset': { borderColor: '#C0C0DD' },
                                        '&.Mui-focused fieldset': { borderColor: '#C0C0DD' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#C0C0DD' }
                                }}
                            />
                        </Box>

                        <Box className="flex justify-between mt-2">
                            <Button
                                variant="outlined"
                                onClick={() => setIsEditing(false)}
                                sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.05)'
                                    }
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSaveTemplate}
                                disabled={loading || !editingTemplate.name || !editingTemplate.content}
                                startIcon={<SaveIcon />}
                                sx={{
                                    backgroundColor: '#C0C0DD',
                                    color: '#252538',
                                    '&:hover': {
                                        backgroundColor: '#d396ed',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'rgba(224, 177, 241, 0.5)',
                                        color: 'rgba(37, 37, 56, 0.5)',
                                    }
                                }}
                            >
                                {loading ? 'Saving...' : 'Save Template'}
                            </Button>
                        </Box>
                    </Box>
                ) : templates.length === 0 ? (
                    <Box className="my-4 text-center py-8">
                        <Typography variant="body1" fontFamily='Inter' className="mb-4 text-gray-300">
                            No message templates have been created yet.
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleCreateNew}
                            sx={{
                                borderColor: '#C0C0DD',
                                color: '#C0C0DD',
                                '&:hover': {
                                    borderColor: '#d396ed',
                                    backgroundColor: 'rgba(224, 177, 241, 0.1)'
                                }
                            }}
                        >
                            Create Your First Template
                        </Button>
                    </Box>
                ) : (
                    // Template Selector
                    <Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Box className="md:col-span-1">
                            <Typography variant="subtitle1" fontFamily='Inter' fontWeight={600} className="mb-2">
                                Your Templates
                            </Typography>
                            <Paper sx={{
                                overflow: 'auto',
                                maxHeight: '40vh',
                                backgroundColor: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(224, 177, 241, 0.2)',
                                borderRadius: '12px'
                            }}>
                                {templates.map((template) => (
                                    <Box
                                        key={template.ID}
                                        onClick={() => handleSelectTemplate(template.ID)}
                                        sx={{
                                            p: 2,
                                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            backgroundColor: selectedTemplateId === template.ID
                                                ? 'rgba(224,177,241,0.1)'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedTemplateId === template.ID
                                                    ? 'rgba(224,177,241,0.15)'
                                                    : 'rgba(255,255,255,0.05)'
                                            }
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body1" fontFamily='Inter' fontWeight={selectedTemplateId === template.ID ? 600 : 400} sx={{ color: 'white' }}>
                                                {template.Name}
                                            </Typography>
                                            {template.Category && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                        display: 'inline-block',
                                                        backgroundColor: 'rgba(224,177,241,0.1)',
                                                        px: 1,
                                                        py: 0.2,
                                                        borderRadius: '4px',
                                                        mt: 0.5
                                                    }}
                                                >
                                                    {template.Category}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditTemplate(template.ID);
                                                }}
                                                sx={{
                                                    color: 'rgba(255,255,255,0.7)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(224,177,241,0.1)',
                                                        color: '#C0C0DD'
                                                    }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedTemplateId(template.ID);
                                                    setShowDeleteConfirm(true);
                                                }}
                                                sx={{
                                                    color: 'rgba(255,255,255,0.7)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,87,87,0.1)',
                                                        color: '#ff5757'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Box>

                        <Box className="md:col-span-2">
                            <Typography variant="subtitle1" fontFamily='Inter' fontWeight={600} className="mb-2">
                                Preview
                            </Typography>
                            <Paper
                                sx={{
                                    p: 3,
                                    height: '40vh',
                                    overflow: 'auto',
                                    backgroundColor: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(224, 177, 241, 0.2)',
                                    borderRadius: '12px',
                                    position: 'relative'
                                }}
                            >
                                {selectedTemplateId ? (
                                    <Typography
                                        variant="body1"
                                        fontFamily='Inter'
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            color: 'white'
                                        }}
                                    >
                                        {templates.find(t => t.ID === selectedTemplateId)?.Content || ''}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.5)',
                                            fontStyle: 'italic',
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)'
                                        }}
                                    >
                                        Select a template to preview
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    bgcolor: 'transparent',
                    px: 3,
                    py: 2,
                    borderTop: '1px solid rgba(224, 177, 241, 0.1)',
                    flex: '0 0 auto'
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.05)'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUseTemplate}
                    disabled={!selectedTemplateId || isEditing}
                    sx={{
                        backgroundColor: '#C0C0DD',
                        color: '#252538',
                        '&:hover': {
                            backgroundColor: '#d396ed',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(224, 177, 241, 0.5)',
                            color: 'rgba(37, 37, 56, 0.5)',
                        }
                    }}
                >
                    Use Template
                </Button>
            </DialogActions>
        </Dialog>
    );
} 